import { ColorPicker } from 'primereact/colorpicker';
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Navigation } from 'swiper/modules';
import { reviewBg } from '@/utils/imagepath';
import { postData } from '@/services/api.service';
import { toast } from 'react-toastify';
import ImageCropper from '@/components/imageCropper/imageCropper';
import { fetchCroppedImage } from '@/utils/constants';
import AIContentWriter from '@/components/AIContentWriter';
import AIImageGenerator from '@/components/AIImageGenerator';
import { setTemplateImages } from '@/core/redux/commonSlice';
import { RootState } from '@/core/redux/store';

function ReviewSectionEditModal({ themeData, getThemeData }: any) {
  const dispatch = useDispatch<any>();
  const [editData, setEditData] = useState<any>();
  const jsonData: any = useSelector<any>((state) => state.homeJsonList);
  const [activeTab, setActiveTab] = useState(0);
  const [cropImageHeight, setCropImageHeight] = useState('');
  const [cropImageWidth, setCropImageWidth] = useState('');
  const [selectedCropImage, setSelectedCropImage] = useState('');
  const [showCropModal, setShowCropModal] = useState<boolean>(false);
  const [imagedata, setImagedata] = useState<any>(null);

  const headerData = useSelector((state: RootState) => state.common.header);
  const templateImages = useSelector((state: RootState) => state.common.templateImages);
  const [template, setTemplate] = useState(headerData?.template);

  const [bannerImages, setBannerImages] = useState<string[]>([]);

  const images: Array<string> = [
    reviewBg,
    reviewBg,
    reviewBg,
    reviewBg,
    reviewBg,
    reviewBg,
    reviewBg,
  ];

  const saveJsonData = async () => {
    const payload = {
      cms_json: JSON.stringify(editData),
    };
    try {
      const response = await postData('save_cms_json', payload);
      if (response.status == 200 || response.status == 201) {
        getThemeData();
        toast.success('Theme Updated Successfully');
      }
    } catch (err) {
      console.error(err);
    } finally {
    }
  };

  const updateImageSectionBanner = async (e: any) => {
    const image = e.target.files[0];
    // setCropImageHeight("150");
    // setCropImageWidth("150");
    // setSelectedCropImage(image);
    // setShowCropModal(true);
    if (image instanceof File) {
      const reader = new FileReader();
      reader.readAsDataURL(image);
      reader.onload = (res: any) => {
        handleCroppedImage(res.target.result);
      };
    }
  };

  const handleCroppedImage = async (image: any) => {
    setShowCropModal(false);
    setImagedata(image);
    const imageLink = await fetchCroppedImage(image);
    if (imageLink != false) {
      handleBannerImageChange(imageLink);
    }
  };

  const handleChange = (e: any) => {
    const { value, name, type, checked } = e.target;
    setEditData((prevJsonData: any) => ({
      ...prevJsonData,
      theme_4: {
        ...prevJsonData.theme_4,
        home: {
          ...prevJsonData.theme_4.home,
          section_3: {
            ...prevJsonData.theme_4.home.section_3,
            [name]: value,
          },
        },
      },
    }));
  };

  const handleBannerImageChange = (image: string) => {
    //Update the banner value in the JSON data
    setEditData((prevJsonData: any) => ({
      ...prevJsonData,
      theme_4: {
        ...prevJsonData.theme_4,
        home: {
          ...prevJsonData.theme_4.home,
          section_3: {
            ...prevJsonData.theme_4.home.section_3,
            banner: image,
          },
        },
      },
    }));
  };

  // Auto-generate prompt based on review section context for Banner
  const generateAutoPromptBanner = (): string => {
    const title = editData?.theme_4?.home?.section_3?.title || '';
    
    // Build context-based prompt
    let contextPrompt = 'modern professional banner image for review section';
    
    // Add banner-specific styling
    contextPrompt += ', clean modern aesthetic, professional photography, suitable for review section banner, wide landscape format, vibrant colors, high quality, no text, no words, no letters, no typography';
    
    return contextPrompt;
  };

  // Handle AI banner image save
  const handleAIImageSave = (imageUrl: string) => {
    // Set the banner image
    handleBannerImageChange(imageUrl);
    
    // Add to banner images array for slider
    setBannerImages(prevImages => [...prevImages, imageUrl]);
    
    // Update Redux store with the new image in banner array
    const templateKey = template ? `template${template}` : 'template4';
    const currentTemplate = templateImages[templateKey] || {};
    const currentBannerImages = currentTemplate.banner || [];
    const updatedTemplateImages = {
      ...templateImages,
      [templateKey]: {
        ...currentTemplate,
        banner: [...currentBannerImages, { url: imageUrl }],
      },
    };
    dispatch(setTemplateImages(updatedTemplateImages));
  };

  useEffect(() => {
    setEditData(themeData);
  }, [themeData]);

  useEffect(() => {
    if (editData != undefined) {
      setEditData(editData);
    }
  }, [editData]);

  // Load existing template images from Redux store with filtering
  useEffect(() => {
    if (templateImages && typeof templateImages === 'object') {
      const templateKey = template ? `template${template}` : 'template4';
      const currentTemplate = templateImages[templateKey];
      const bannerImgs = currentTemplate?.banner || [];
      
      // Helper function to load image dimensions
      const loadImageDimensions = async (url: string): Promise<{ url: string; width: number; height: number } | null> => {
        return new Promise((resolve) => {
          const img = new Image();
          img.onload = () => {
            resolve({ url, width: img.width, height: img.height });
          };
          img.onerror = () => {
            resolve(null);
          };
          img.src = url;
        });
      };

      // Load and filter background images (wide/landscape from banner array)
      if (Array.isArray(bannerImgs) && bannerImgs.length > 0) {
        const filterBannerImages = async () => {
          const imagePromises = bannerImgs.map((img: any) => loadImageDimensions(img.url));
          const loadedImages = await Promise.all(imagePromises);
          
          // Filter for wide/landscape images (aspect ratio > 1.3)
          const wideImages = loadedImages
            .filter((img) => {
              if (img === null) return false;
              const aspectRatio = img.width / img.height;
              return aspectRatio > 1.3;
            })
            .map((img) => img!.url);
          
          setBannerImages(wideImages);
        };

        filterBannerImages();
      } else {
        setBannerImages([]);
      }
    } else {
      setBannerImages([]);
    }
  }, [templateImages, template]);

  return (
    <>
      {/* <Draggable> */}
      <div className="modal-dialog modal-lg modal-dialog-centered theme-edit-modal">
        <div className="modal-content">
          <div className="modal-header">
            <h5 className="modal-title">Section3 Theme Edit</h5>
            <button
              type="button"
              className="btn-close"
              data-bs-dismiss="modal"
              aria-label="Close"
            ></button>
          </div>
          <div className="modal-content modal-body">
            <div className="edit-section">
              <div className="row">
                <div className="col-md">
                  <div className="mb-3">
                    <div className="d-flex align-items-center mb-2 flex-wrap gap-2">
                      <label className="form-label mb-0 mr-2">
                        Title <small>(max 250 char)</small>
                      </label>
                      <AIContentWriter
                        onContentGenerated={(content) => {
                          handleChange({
                            target: {
                              name: 'title',
                              value: content,
                            },
                          });
                        }}
                        contentType="hero_section"
                        placeholder="Describe what you want in your title. Example: 'Write a catchy title for customer reviews section'"
                        context="This is for the review section title"
                      />
                    </div>
                    <input
                      type="text"
                      className="form-control"
                      name="title"
                      value={editData?.theme_4?.home?.section_3?.title}
                      onChange={handleChange}
                      maxLength={250}
                    />
                  </div>
                </div>
                <div className="col-md-auto">
                  <div className="mb-3">
                    <label className="form-label">Color</label>
                    <div className="input-group color-change">
                      <input
                        type="text"
                        className="form-control"
                        name="h2color"
                        value={editData?.theme_4?.home?.section_3?.h2color}
                        onChange={handleChange}
                      />
                      <ColorPicker
                        name="h2color"
                        format="hex"
                        onChange={handleChange}
                        value={editData?.theme_4?.home?.section_3?.h2color}
                      />
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col">
                  <div className="mb-1">
                    <div className="row">
                      <div className="col-md-12">
                        <label className="form-label">Background Image</label>
                        <div className="img-bg active">
                          <img
                            src={editData?.theme_4?.home?.section_3?.banner}
                            alt="Hero Image"
                          />
                        </div>
                        <div className="img-options">
                          <div className="row">
                            <div className="col-md-12">
                              {bannerImages.length > 0 ? (
                                <Swiper
                                  spaceBetween={15}
                                  navigation={true}
                                  modules={[Navigation]}
                                  breakpoints={{
                                    640: {
                                      slidesPerView: 1,
                                    },
                                    768: {
                                      slidesPerView: 4,
                                    },
                                    1024: {
                                      slidesPerView: 6,
                                    },
                                  }}
                                  className="homejson-slider"
                                >
                                  {bannerImages.map((imageUrl: string, idx: number) => {
                                    return (
                                      <SwiperSlide key={idx}>
                                        <div
                                          className="img-option"
                                          onClick={() =>
                                            handleBannerImageChange(imageUrl)
                                          }
                                        >
                                          <img
                                            src={imageUrl}
                                            alt="Hero Image"
                                            className="img-fluid"
                                          />
                                        </div>
                                      </SwiperSlide>
                                    );
                                  })}
                                </Swiper>
                              ) : (
                                <div className="empty-slider-state text-center py-4">
                                  <div className="empty-state-content">
                                    <i className="fas fa-images fa-3x text-muted mb-3"></i>
                                    <h6 className="text-muted mb-2">No Background Images Available</h6>
                                    <p className="text-muted small mb-3">
                                      Generate AI images or upload your own to see them here
                                    </p>
                                  </div>
                                </div>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* AI Image Generator */}
              <AIImageGenerator
                generateAutoPrompt={generateAutoPromptBanner}
                onSave={handleAIImageSave}
                imageType="banner"
                defaultWidth="1792"
                defaultHeight="512"
                title="AI Banner"
                autoGenerate={true}
                template={template}
              />

              <div className="row">
                <div className="col">
                  <div className="mb-3">
                    <label className="form-label">Upload Image</label>
                    <input
                      type="file"
                      accept="image/png, image/jpeg"
                      className="form-control"
                      name="image"
                      onChange={updateImageSectionBanner}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="mb-3">
              <button
                type="submit"
                data-bs-dismiss="modal"
                className="btn primary-btn"
                onClick={() => {
                  saveJsonData();
                }}
              >
                Save Changes
              </button>
            </div>
          </div>
        </div>
      </div>
      {/* </Draggable> */}
      {showCropModal ? (
        <>
          <div
            className="modal modal-sm-crop fade show onboarding-modal settings cropModal"
            id="crop-modal"
            tabIndex={-1}
            aria-labelledby="crop-modal"
            aria-hidden="true"
            style={{ display: 'block' }}
          >
            <ImageCropper
              src={selectedCropImage}
              height={cropImageHeight}
              width={cropImageWidth}
              handleCrop={handleCroppedImage}
              close={() => {
                setShowCropModal(false);
              }}
            />
          </div>
        </>
      ) : null}
    </>
  );
}

export default ReviewSectionEditModal;
